// Findings whose id is derived from the thing that was found.
//
// These are the second of the two categories in the finding vocabulary, and the
// reason `detection-rules.ts` is not the whole story. Every other rule has a
// fixed id you can read in a table. These do not: a scan that detects an
// action, a data flow, an embedded payload, a YARA rule, a fingerprint, a
// known hash or a vulnerable dependency mints one finding per thing observed,
// and the id *is* the observation. There is no table to write, because the
// number of rows is not known until the archive has been read.
//
// The line between the categories is therefore not "static vs dynamic" — it is
// "does the id exist before the scan runs". `JARLAB-OBF-005` exists;
// `JARLAB-DEP-GHSA-7rjr-3q55-vv33` does not.
//
// `RULE_IDS` in `detection-rules.ts` lists both categories, so a reader who
// wants every id the product can emit starts there and lands here.

import {
  severityForAdvisory,
} from "./advisories/match";
import type { RuleSink } from "./detection-rules";
import type { detectActions } from "./action-detector";
import type { analyzeSymbolicDataFlow } from "./symbolic-engine";
import type { inspectEmbeddedPayloads } from "./payload-inspector";
import type { scanYara } from "./yara";
import type {
  detectFamilySignatures,
  matchFingerprints,
  matchKnownHashes,
} from "./threat-intel";
import type { checkDependencyAdvisories } from "./advisories/match";
import type {
  DependencyAdvisory,
  Finding,
} from "./types";

type DetectedAction = ReturnType<typeof detectActions>[number];
type DataFlowTrace = ReturnType<typeof analyzeSymbolicDataFlow>["traces"][number];
type PayloadCandidate = ReturnType<typeof inspectEmbeddedPayloads>[number];
type YaraMatch = ReturnType<typeof scanYara>[number];
type FingerprintMatch = ReturnType<typeof matchFingerprints>[number];
type KnownHashMatch = ReturnType<typeof matchKnownHashes>[number];
type FamilySignatureMatch = ReturnType<typeof detectFamilySignatures>[number];
type AdvisoryMatch = Awaited<
  ReturnType<typeof checkDependencyAdvisories>
>["matches"][number];

/** The action ids that become findings, and the category each maps to. */
const REPORTABLE_ACTIONS = new Set([
  "minecraft-token-read",
  "minecraft-token-field",
  "minecraft-pay-send",
  "minecraft-pay-literal",
  "remote-download-to-disk",
  "archive-extraction",
  "process-execution",
  "download-execute-chain",
  "minecraft-session-exfiltration",
  "minecraft-account-file-read",
  "screen-capture",
  "clipboard-read",
  "keyboard-capture",
  "minecraft-log-submit-route",
  "hardcoded-payload-location",
  "hardcoded-download-command",
  "security-product-tampering",
  "scheduled-task-persistence",
  "startup-persistence",
  "defense-tampering-persistence-chain",
  "jar-signature-stripping",
  "sandbox-process-discovery",
]);

/**
 * One finding per observed action that an analyst should see. The id is the
 * action id, so a new action gets a new finding without a rule being added.
 */
export function emitActionFindings(
  actions: DetectedAction[],
  input: { hasReachabilityRoots: boolean },
  add: RuleSink,
) {
  const actionFindingCategory: Record<
    DetectedAction["category"],
    Finding["category"]
  > = {
    minecraft: "collection",
    credential: "credentials",
    command: "execution",
    download: "network",
    execution: "execution",
    network: "network",
    collection: "collection",
    filesystem: "structure",
    persistence: "persistence",
    evasion: "evasion",
  };
  for (const action of actions) {
    if (!REPORTABLE_ACTIONS.has(action.actionId)) continue;
    if (input.hasReachabilityRoots && !action.reachable) continue;
    add(
      {
        id: `JARLAB-ACTION-${action.actionId.toUpperCase()}`,
        severity: action.severity,
        confidence: action.confidence,
        category: actionFindingCategory[action.category],
        title: action.title,
        description: action.description,
        recommendation:
          action.severity === "critical" || action.severity === "high"
            ? "Trace the complete call path and keep the sample quarantined until this behavior is explained."
            : "Review the surrounding method and verify that this behavior is required by the package.",
        tags: ["observed-action", ...action.tags],
        evidence: [
          ...(action.method ? [`Method: ${action.method}`] : []),
          ...(action.reachable
            ? [
                `Reachability: ${action.entryPoint ?? "supported entry point"}`,
              ]
            : []),
          ...(action.destinations ?? []).map(
            (destination) => `Destination: ${destination}`,
          ),
          ...(action.techniques ?? []).map(
            (technique) => `${technique.id}: ${technique.name}`,
          ),
          ...action.evidence,
        ],
      },
      action.entry,
    );
  }
}

/**
 * One finding per proven data flow. The id names the source and sink kinds.
 */
export function emitFlowFindings(
  flows: DataFlowTrace[],
  input: { hasReachabilityRoots: boolean },
  add: RuleSink,
) {
  const flowFindingCategory: Record<
    DataFlowTrace["sinkKind"],
    Finding["category"]
  > = {
    "http-request": "network",
    "socket-write": "network",
    webhook: "network",
    "process-execution": "execution",
    "dynamic-class-load": "execution",
    "file-write": "structure",
    "chat-command": "execution",
    "clipboard-write": "collection",
    unknown: "structure",
  };
  for (const flow of flows) {
    if (flow.severity === "low") continue;
    if (input.hasReachabilityRoots && !flow.reachable) continue;
    add(
      {
        id: `JARLAB-FLOW-${flow.sourceKind.toUpperCase()}-${flow.sinkKind.toUpperCase()}`,
        severity: flow.severity,
        confidence: flow.confidence,
        category: flowFindingCategory[flow.sinkKind],
        title: flow.title,
        description: flow.description,
        recommendation:
          flow.severity === "critical"
            ? "Treat the path as hostile, quarantine the JAR, and inspect any machine where it ran."
            : "Open the data-flow trace and validate every source, transform, call, and sink on the path.",
        tags: flow.tags,
        evidence: [
          `Source: ${flow.sourceLabel}`,
          `Sink: ${flow.sinkLabel}`,
          `Reachability: ${flow.reachable ? flow.entryPoint ?? "entry-point reachable" : "not proven"}`,
          ...flow.recoveredValues.slice(0, 3).map((value) => `Value: ${value}`),
          ...flow.steps
            .slice(-4)
            .map(
              (step) =>
                `${step.className}.${step.method} @ ${step.offset ?? "?"}: ${step.detail}`,
            ),
        ],
      },
      flow.entry,
    );
  }
}

/**
 * One finding per structurally inspected embedded payload. The id is the
 * payload type.
 */
export function emitPayloadFindings(
  payloads: PayloadCandidate[],
  input: { referencedByReachableCode: (path: string) => boolean },
  add: RuleSink,
) {
  for (const payload of payloads) {
    if (payload.risk === "context") continue;
    if (!input.referencedByReachableCode(payload.path)) continue;
    add(
      {
        id: `JARLAB-PAYLOAD-${payload.type.toUpperCase()}`,
        severity:
          payload.risk === "critical"
            ? "critical"
            : payload.risk === "high"
              ? "high"
              : "medium",
        confidence: payload.capabilities.length ? "high" : "medium",
        category: payload.executable ? "execution" : "structure",
        title: `Embedded ${payload.type} payload`,
        description:
          "JarLab identified and structurally inspected an embedded executable or script without launching it.",
        recommendation:
          "Review the payload’s capabilities, strings, indicators, architecture, and hash before allowing the parent JAR to run.",
        tags: ["embedded-payload", payload.type, ...payload.capabilities],
        evidence: [
          `SHA-256: ${payload.sha256}`,
          `Entropy: ${payload.entropy.toFixed(2)} bits/byte`,
          ...payload.details.slice(0, 4),
          ...payload.capabilities.slice(0, 6),
          ...payload.indicators.slice(0, 3),
        ],
      },
      payload.path,
    );
  }
}

/**
 * One finding per YARA rule that matched. The id is the analyst's own rule id,
 * not a `JARLAB-*` one, which is why this family cannot be a rule table.
 */
export function emitYaraFindings(
  matches: YaraMatch[],
  add: RuleSink,
) {
  for (const match of matches) {
    add(
      {
        id: match.ruleId,
        severity: match.severity,
        confidence: match.confidence,
        category: "signature",
        title: `YARA rule ${match.rule}`,
        description:
          match.meta.description ??
          `The analyst-supplied YARA rule "${match.rule}" matched this archive. Its condition held for at least one scanned surface, so the strings it names are present together somewhere in the sample.`,
        evidence: match.evidence,
        tags: ["yara", ...match.tags, ...(match.tags.length ? [] : ["custom-rule"])],
        recommendation:
          match.meta.reference ??
          "Review the matched surface and the rule's own intent before trusting it. A YARA hit means the strings co-occur, not that the sample is malicious.",
      },
      match.target,
    );
  }
}

/**
 * One finding per structural fingerprint match. The id is the matched family.
 */
export function emitFingerprintFindings(
  matches: FingerprintMatch[],
  add: RuleSink,
) {
  for (const match of matches) {
    const trusted = match.trust !== "local-unverified";
    add(
      {
        id: `JARLAB-FP-${match.family.toUpperCase().replace(/\W+/g, "-")}`,
        severity: trusted ? "critical" : "medium",
        confidence: trusted ? "high" : "low",
        category: "signature",
        title: trusted
          ? `Repacked ${match.family} structural match`
          : `Unverified local ${match.family} structural match`,
        description: trusted
          ? `The archive is a ${match.similarity.toFixed(1)}% structural match to a known ${match.family} sample (${match.variant}). The bytes differ, so this is a rebuild or repack of the same program rather than an exact hit.`
          : `The archive is a ${match.similarity.toFixed(1)}% structural match to a record in an intelligence pack that was not anchored to a trusted Ed25519 public key. It is evidence for review, not proof of malware.`,
        recommendation: trusted
          ? "Quarantine the file and treat it as the same campaign: a repack usually carries the same intent even where the constants and class names have been rewritten."
          : "Validate the match against an authoritative source or reinstall the intelligence pack with a known signing key.",
        tags: [
          "known-fingerprint",
          trusted ? "malware" : "unverified-intelligence",
          match.family.toLowerCase(),
          "repack",
        ],
        evidence: [
          `Structural similarity: ${match.similarity.toFixed(1)}% (pack threshold ${match.threshold.toFixed(1)}%)`,
          `Reference simhash: ${match.simhash64}`,
          `Source: ${match.source}${match.firstSeen ? ` · first seen ${match.firstSeen}` : ""}`,
          `Trust: ${match.trust ?? "built-in"}`,
        ],
      },
      "archive-wide similarity",
    );
  }
}

/**
 * One finding per known-hash hit. The id is the matched family.
 */
export function emitKnownHashFindings(
  matches: KnownHashMatch[],
  add: RuleSink,
) {
  for (const match of matches) {
    const trusted = match.trust !== "local-unverified";
    add(
      {
        id: `JARLAB-HASH-${match.family.toUpperCase().replace(/\W+/g, "-")}`,
        severity: trusted ? "critical" : "medium",
        confidence: trusted ? "high" : "low",
        category: "signature",
        title: trusted
          ? `Known ${match.family} sample hash`
          : `Unverified local ${match.family} hash match`,
        description:
          trusted
            ? `The ${match.target} exactly matches a threat-intelligence record for ${match.family} (${match.variant}).`
            : `The ${match.target} matches an analyst-imported record that was not anchored to a trusted Ed25519 public key. It is evidence for review, not proof of malware.`,
        recommendation:
          trusted
            ? "Quarantine the file. Do not execute it, and investigate any system where it has already run."
            : "Validate the hash against an authoritative source or reinstall the intelligence pack with a known signing key.",
        tags: [
          "known-hash",
          trusted ? "malware" : "unverified-intelligence",
          match.family.toLowerCase(),
        ],
        evidence: [
          `${match.algorithm.toUpperCase()}: ${match.hash}`,
          `Target: ${match.entry ?? match.target}`,
          `Source: ${match.source}${match.firstSeen ? ` · first seen ${match.firstSeen}` : ""}`,
          `Trust: ${match.trust ?? "built-in"}`,
        ],
      },
      match.entry ?? match.target,
    );
  }
}

/**
 * One finding per family-signature match. The id is the signature id from
 * `threat-intel`.
 */
export function emitFamilySignatureFindings(
  matches: FamilySignatureMatch[],
  add: RuleSink,
) {
  for (const match of matches) {
    add(
      {
        id: match.signatureId,
        severity: match.confidence === "high" ? "critical" : "high",
        confidence: match.confidence,
        category: "signature",
        title: `${match.family} ${match.variant} signature`,
        description:
          `${match.matchedSignals.length} independent static signals match the ${match.family} ${match.variant} profile (${match.score}/${match.maxScore} signature score).`,
        recommendation:
          "Keep the sample quarantined and validate the matched classes, resources, and infrastructure against the linked evidence in the report.",
        tags: [
          "family-signature",
          match.family.toLowerCase(),
          match.variant.toLowerCase().replace(/\s+/g, "-"),
        ],
        evidence: match.matchedSignals
          .slice(0, 8)
          .map(
            (signal) =>
              `${signal.label} · ${signal.source} · ${signal.evidence}`,
          ),
      },
      `${match.family} signature`,
    );
  }
}

/**
 * One finding per vulnerable dependency, plus the `dependencyAdvisories` rows
 * the report and the SBOM both read. The id is the upstream advisory id, which
 * is why the finding can be cited rather than merely counted.
 *
 * Returns rather than mutating: the caller owns the list it ends up in, so
 * there is one place that decides what a scan reports about dependencies.
 */
export function emitDependencyFindings(
  input: { matches: AdvisoryMatch[]; dataset: { fetchedAt?: string } | null },
  add: RuleSink,
): DependencyAdvisory[] {
  const { matches, dataset } = input;
  const out: DependencyAdvisory[] = [];
    for (const match of matches) {
      const { advisory, dependency } = match;
      const { severity, rated } = severityForAdvisory(advisory);
      const aliases = advisory.aliases.filter((alias) => alias.startsWith("CVE-"));
      out.push({
        coordinate: `${dependency.groupId}:${dependency.artifactId}:${dependency.version}`,
        advisoryId: advisory.id,
        aliases,
        summary: advisory.summary,
        cvssScore: advisory.cvssScore,
        cvssVector: advisory.cvssVector,
        fixedVersions: advisory.fixedVersions,
        tier: match.tier,
        coordinateWasRead: match.coordinateWasRead,
        severity,
        rated,
      });
      const identity = [
        advisory.id,
        ...aliases,
        ...(match.tier === "exact" ? [] : ["via OSV range"]),
      ].join(" · ");
      const score = advisory.cvssScore === null
        ? "no CVSS published upstream"
        : `CVSS ${advisory.cvssScore} (${advisory.cvssVector})`;
      const fix = advisory.fixedVersions.length
        ? advisory.fixedVersions.join(", ")
        : "no fixed version published";
      add(
        {
          id: `JARLAB-DEP-${advisory.id}`,
          severity,
          confidence: match.coordinateWasRead ? "high" : "medium",
          category: "dependency",
          title: `${dependency.artifactId} ${dependency.version} is affected by ${advisory.id}`,
          description: [
            advisory.summary || "Known vulnerability affecting this exact version.",
            `Advisory: ${identity}`,
            `Severity: ${score}`,
            `Fixed in: ${fix}`,
            match.coordinateWasRead
              ? ""
              : "Coordinate was inferred from the entry path rather than read from it, so confirm the version before acting.",
          ]
            .filter(Boolean)
            .join("\n"),
          recommendation: advisory.fixedVersions.length
            ? `Upgrade ${dependency.groupId}:${dependency.artifactId} to ${advisory.fixedVersions[0]} or later.`
            : `No fixed release is published for ${advisory.id}; review the advisory and weigh mitigations.`,
          tags: ["dependency", "vulnerability", advisory.id.toLowerCase()],
          evidence: [
            `${dependency.groupId}:${dependency.artifactId}:${dependency.version}`,
            advisory.summary,
            `CVSS ${advisory.cvssScore ?? "not published"} · fixed in ${advisory.fixedVersions.join(", ") || "nothing published"}`,
            rated
              ? `Dataset cutoff ${dataset?.fetchedAt ?? "unknown"}`
              : "Unrated upstream: no scoreable CVSS vector was published for this advisory.",
          ].filter(Boolean),
        },
        dependency.source,
      );
    }
  return out;
}
